import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, FileText, SlidersHorizontal, X } from "lucide-react";
import { useDocuments } from "@/hooks/use-documents";
import type { ChatFilters } from "@/hooks/use-chat";
import { cn } from "@/lib/utils";

interface ChatFiltersBarProps {
  projectId: string;
  filters: ChatFilters;
  onChange: (filters: ChatFilters) => void;
  disabled?: boolean;
}

// Cuenta cuántos grupos de filtros están activos para el badge del botón.
function countActiveFilters(filters: ChatFilters): number {
  let n = 0;
  if (filters.email_sender && filters.email_sender.trim()) n += 1;
  if (filters.date_from) n += 1;
  if (filters.date_to) n += 1;
  if (filters.document_ids && filters.document_ids.length > 0) n += 1;
  return n;
}

export default function ChatFiltersBar({
  projectId,
  filters,
  onChange,
  disabled = false,
}: ChatFiltersBarProps) {
  const [open, setOpen] = useState(false);
  const [docDropdownOpen, setDocDropdownOpen] = useState(false);
  const docDropdownRef = useRef<HTMLDivElement>(null);

  // Documentos COMPLETADOS del proyecto, para el selector.
  const documentsQuery = useDocuments(projectId, {
    estado: "COMPLETADO",
    size: 100,
  });
  const completedDocs = useMemo(
    () => documentsQuery.data?.items ?? [],
    [documentsQuery.data]
  );

  const selectedIds = filters.document_ids ?? [];
  const activeCount = countActiveFilters(filters);

  // Cerrar el dropdown de documentos al hacer click fuera.
  useEffect(() => {
    if (!docDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        docDropdownRef.current &&
        !docDropdownRef.current.contains(e.target as Node)
      ) {
        setDocDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [docDropdownOpen]);

  const toggleDoc = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange({
      ...filters,
      document_ids: next.length > 0 ? next : undefined,
    });
  };

  const clearAll = () => {
    onChange({});
    setDocDropdownOpen(false);
  };

  const selectedDocNames = useMemo(() => {
    const byId = new Map(completedDocs.map((d) => [d.id, d.filename]));
    return selectedIds.map((id) => ({
      id,
      name: byId.get(id) ?? "Documento",
    }));
  }, [selectedIds, completedDocs]);

  return (
    <div className="border-t border-line bg-surface">
      <div className="flex items-center gap-2 px-4 pt-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
            activeCount > 0
              ? "border-brand/40 bg-brand/10 text-brand-hover"
              : "border-line text-content-muted hover:text-ink hover:border-line-strong"
          )}
          aria-expanded={open}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          <span>Filtros</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-pill text-[10px] font-semibold bg-brand text-white">
              {activeCount}
            </span>
          )}
          <ChevronDown
            className={cn(
              "w-3.5 h-3.5 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>

        {activeCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-xs text-content-muted hover:text-danger transition-colors disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" />
            Limpiar filtros
          </button>
        )}
      </div>

      {open && (
        <div className="px-4 py-3 mt-3 mx-4 mb-1 rounded-lg border border-line bg-surface-100 space-y-3">
          {/* Remitente */}
          <div>
            <label className="block text-[11px] font-medium text-content-muted uppercase tracking-wide mb-1">
              Remitente
            </label>
            <input
              type="text"
              value={filters.email_sender ?? ""}
              onChange={(e) =>
                onChange({
                  ...filters,
                  email_sender: e.target.value || undefined,
                })
              }
              placeholder="ej. carmen@..."
              disabled={disabled}
              className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-50"
            />
          </div>

          {/* Rango de fechas */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-content-muted uppercase tracking-wide mb-1">
                Desde
              </label>
              <input
                type="date"
                value={filters.date_from ?? ""}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    date_from: e.target.value || undefined,
                  })
                }
                disabled={disabled}
                className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-content-muted uppercase tracking-wide mb-1">
                Hasta
              </label>
              <input
                type="date"
                value={filters.date_to ?? ""}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    date_to: e.target.value || undefined,
                  })
                }
                disabled={disabled}
                className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-50"
              />
            </div>
          </div>

          {/* Documentos */}
          <div>
            <label className="block text-[11px] font-medium text-content-muted uppercase tracking-wide mb-1">
              Documentos
            </label>
            <div className="relative" ref={docDropdownRef}>
              <button
                type="button"
                onClick={() => setDocDropdownOpen((v) => !v)}
                disabled={disabled || completedDocs.length === 0}
                className="w-full flex items-center justify-between px-3 py-2 rounded border border-line bg-surface text-sm text-left focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  className={cn(
                    "truncate",
                    selectedIds.length === 0 && "text-content-muted"
                  )}
                >
                  {completedDocs.length === 0
                    ? "No hay documentos completados"
                    : selectedIds.length === 0
                      ? "Todos los documentos"
                      : `${selectedIds.length} seleccionado${
                          selectedIds.length > 1 ? "s" : ""
                        }`}
                </span>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 flex-shrink-0 text-content-muted transition-transform",
                    docDropdownOpen && "rotate-180"
                  )}
                />
              </button>

              {docDropdownOpen && completedDocs.length > 0 && (
                <div className="absolute z-20 bottom-full mb-1 w-full max-h-56 overflow-y-auto rounded border border-line bg-surface shadow-card">
                  <ul className="py-1">
                    {completedDocs.map((doc) => {
                      const checked = selectedIds.includes(doc.id);
                      return (
                        <li key={doc.id}>
                          <label className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-surface-100">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleDoc(doc.id)}
                              className="rounded border-line text-brand focus:ring-brand/30"
                            />
                            <FileText className="w-3.5 h-3.5 flex-shrink-0 text-content-muted" />
                            <span
                              className="truncate text-content"
                              title={doc.filename}
                            >
                              {doc.filename}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Chips de documentos seleccionados */}
            {selectedDocNames.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedDocNames.map((doc) => (
                  <span
                    key={doc.id}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-pill text-[11px] bg-brand/10 text-brand-hover max-w-[16rem]"
                  >
                    <span className="truncate" title={doc.name}>
                      {doc.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleDoc(doc.id)}
                      className="flex-shrink-0 hover:text-danger"
                      aria-label={`Quitar ${doc.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
